/* eslint-disable max-lines-per-function */
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  ScrollArea,
} from '@e2n/ui';
import {
  getWiederholungStringFromTerminWiederholungsIntervall,
  TerminWeekdayOccurrence,
  TerminWiederholungsIntervall,
} from '@/domain/termine/shared/types';
import { useCreateTermineFormContext } from '@/domain/termine/features/CreateTermin/shared/useCreateTermineFormContext';
import { faChevronDown, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { useDrawerSelection } from '@/domain/termine/features/CreateTermin/Drawer/hooks/useDrawerSelection';
import { useState } from 'react';

const validIntervals = Object.values(TerminWiederholungsIntervall);

interface WiederholungsIntervallDrawerProps {
  value: TerminWiederholungsIntervall;
  onChange: (value: TerminWiederholungsIntervall) => void;
}

function isTerminWiederholungsIntervall(
  value: string,
): value is TerminWiederholungsIntervall {
  return validIntervals.some((interval) => interval === value);
}

export default function IndividualRepetitionIntervalDrawer({
  value,
  onChange,
}: WiederholungsIntervallDrawerProps) {
  const { form } = useCreateTermineFormContext();
  const { localSelection, handleOpenChange, handleSelect } =
    useDrawerSelection<TerminWiederholungsIntervall>(value);
  const [open, setOpen] = useState(false);

  return (
    <form.Subscribe selector={(state) => state.values.wochentagAuftreten}>
      {(selectedWeekdayOccurrence) => {
        const handleMonthOrYearInterval = () => {
          form.setFieldValue('wochentagAuswahl', []);
          if (!selectedWeekdayOccurrence) {
            form.setFieldValue(
              'wochentagAuftreten',
              TerminWeekdayOccurrence.FIXER_TAG,
            );
          }
        };

        const intervalHandlers: Record<
          TerminWiederholungsIntervall,
          () => void
        > = {
          [TerminWiederholungsIntervall.TAG]: () => {
            form.setFieldValue('wochentagAuftreten', undefined);
            form.setFieldValue('wochentagAuswahl', []);
          },
          [TerminWiederholungsIntervall.WOCHE]: () => {
            form.setFieldValue('wochentagAuftreten', undefined);
          },
          [TerminWiederholungsIntervall.MONAT]: handleMonthOrYearInterval,
          [TerminWiederholungsIntervall.JAHR]: handleMonthOrYearInterval,
        };

        function handleIntervalChange(selectedInterval: string) {
          if (!isTerminWiederholungsIntervall(selectedInterval)) {
            return;
          }
          onChange(selectedInterval);
          intervalHandlers[selectedInterval]();
        }

        return (
          <div className="pb-3 h-10">
            <Drawer
              open={open}
              onOpenChange={(isOpen) => {
                setOpen(isOpen);
                handleOpenChange(isOpen);
              }}>
              <DrawerTrigger asChild>
                <div className="flex gap-4">
                  <Button
                    variant="outline-secondary"
                    className="w-full justify-between">
                    <span className="text-sm font-normal">
                      {getWiederholungStringFromTerminWiederholungsIntervall(
                        localSelection,
                      )}
                    </span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="text-neutrals-400"
                    />
                  </Button>
                </div>
              </DrawerTrigger>
              <DrawerContent className="flex flex-col">
                <DrawerHeader className="shrink-0">
                  <DrawerTitle className="text-sm text-center text-neutrals-900 font-semibold">
                    {t('domain.termine.frequency')}
                  </DrawerTitle>
                  <DrawerDescription className="sr-only">
                    {t('domain.termine.frequency')}
                  </DrawerDescription>
                </DrawerHeader>
                <ScrollArea className="flex-1 p-4" data-vaul-no-drag>
                  <div className="space-y-0">
                    {Object.values(TerminWiederholungsIntervall).map(
                      (enumValue, idx) => {
                        const isSelected = localSelection === enumValue;
                        return (
                          <div key={enumValue}>
                            <Button
                              variant="ghost"
                              aria-selected={isSelected}
                              onClick={() => {
                                handleSelect(enumValue);
                                handleIntervalChange(enumValue);
                                setOpen(false);
                              }}
                              className="w-full justify-between">
                              <span className="text-sm font-normal">
                                {getWiederholungStringFromTerminWiederholungsIntervall(
                                  enumValue,
                                )}
                              </span>
                              {isSelected && (
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="text-primary-blue-600"
                                />
                              )}
                            </Button>
                            {idx <
                              Object.values(TerminWiederholungsIntervall)
                                .length -
                                1 && (
                              <div className="border-b border-neutrals-300" />
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                </ScrollArea>
              </DrawerContent>
            </Drawer>
          </div>
        );
      }}
    </form.Subscribe>
  );
}
